// Import 3rd-party dependencies
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { ActivatedRoute } from '@angular/router';
import { NgbCarouselConfig } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, interval, of, timer } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';

// Import project-specific dependencies
import { InsightsService } from '@app/core/analytics/services/app-insights-service';
import { HUB_COMPONENT_CONSTS } from '@app/config/constants';
import { PeopleService } from '@app/services/people/people.service';
import { DdUser, UserProfileService } from '@app/services/user-profile/user-profile.service';

// setup for valid date format
const AppDateFormats = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' }
  },
  display: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
    monthYearLabel: { year: 'numeric' }
  }
};

@Component({
  selector: 'app-hub',
  templateUrl: './hub.component.html',
  styleUrls: ['./hub.component.css'],
  providers: [
    NgbCarouselConfig, // add NgbCarouselConfig to the component providers
    {
      provide: MAT_DATE_FORMATS,
      useValue: AppDateFormats
    } // add for MMM DD, YYYY of datepicker input
  ]
})
export class HubComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  greeting: string;
  isShow: string;
  preferredName: string;
  @ViewChild('HubFavorites') hubFavorites: ElementRef;
  @ViewChild('WorkProfile') hubProfile: ElementRef;

  consts = HUB_COMPONENT_CONSTS;

  greeting$ = this.peopleService.currentUser$.pipe(
    switchMap((user) =>
      combineLatest([
        of(user),
        this.profileService
          .getDdProfile(user.employeeId)
          .pipe(catchError(() => of({ core_id: user.employeeId } as DdUser)))
      ]).pipe(
        map(([graphUser, ddUser]) => {
          const graphFirstName = graphUser?.givenName;
          const ddFirstName = ddUser?.preferred_first_name || ddUser?.first_name;
          const preferredName = (ddFirstName || graphFirstName || '').replace(
            this.consts.NAME_REGEX,
            (name: string, first: string, rest: string) => {
              return first.toUpperCase() + rest.toLowerCase();
            }
          );
          return `, ${preferredName}`;
        })
      )
    )
  );

  // separate greeting message from user fetching
  // and set interval of updating greeting message as 1 second
  greetingMessage$ = interval(this.consts.GREETING_SUBSCRIPTION_INTERVAL).pipe(
    startWith(0),
    switchMap(() => {
      const currentHour = new Date().getHours();
      let greeting = '';
      if (currentHour >= 0 && currentHour < 12) {
        greeting = this.consts.GREETING_MORNING;
      } else if (currentHour >= 12 && currentHour < 18) {
        greeting = this.consts.GREETING_AFTERNOON;
      } else {
        greeting = this.consts.GREETING_EVENING;
      }
      return of(greeting);
    })
  );

  constructor(
    config: NgbCarouselConfig,
    private trackingService: InsightsService,
    private peopleService: PeopleService,
    private profileService: UserProfileService,
    private activatedRouter: ActivatedRoute
  ) {
    config.interval = this.consts.CONFIG_INTERVAL;
    config.keyboard = true;
    config.pauseOnHover = true;
  }

  ngOnInit(): void {
    // set active tab
    this.isShow = this.consts.VALUE_DXCHUB;
    localStorage.setItem('isShow', this.isShow);
    this.trackingService.trackPageView('DXC Hub');

    // go to tools and resources section
    this.subs.sink = this.activatedRouter.fragment.subscribe((res) => {
      if (res === 'WorkProfile') {
        this.gotoHubProfileSection()
      } else if (res === 'tools') {
        this.gotoToolsSection(res);
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // comment unused code
  // onViewProfileClick(): void {
  //   this.showMyHubContent();
  // }

  // DXC hub button click
  showDxcHubContent(): void {
    this.trackingService.trackPageView('DXC Hub');
    this.isShow = this.consts.VALUE_DXCHUB;
    localStorage.setItem('isShow', this.isShow);
  }

  // My hub button click
  showMyHubContent(): void {
    this.trackingService.trackPageView('My Hub');
    this.isShow = this.consts.VALUE_MYHUB;
    localStorage.setItem('isShow', this.isShow);
  }

  gotoFavoritesSection(): void {
    let tabTransitionTimeOut = 0;
    let offsetTimeout = 0;

    if (this.isShow !== this.consts.VALUE_MYHUB) {
      document.getElementById('MyHubButton').click();
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
      tabTransitionTimeOut = this.consts.FAVORITES_SECTION_TRANSITION_TIMEOUT;
      offsetTimeout = this.consts.OFFSET_TIMEOUT;
    }

    this.subs.sink = timer(tabTransitionTimeOut).subscribe(() => {
      this.subs.sink = timer(offsetTimeout).subscribe(() => {
        const element = this.hubFavorites.nativeElement as HTMLElement;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  gotoHubProfileSection(): void {
    let tabTransitionTimeOut = 0;
    let offsetTimeout = 0;

    if (this.isShow !== this.consts.VALUE_MYHUB) {
      document.getElementById('MyHubButton').click();
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
      tabTransitionTimeOut = this.consts.HUB_WORK_PROFILE_TRANSITION_TIMEOUT;
      offsetTimeout = this.consts.OFFSET_TIMEOUT;
    }

    this.subs.sink = timer(tabTransitionTimeOut).subscribe(() => {
      this.subs.sink = timer(offsetTimeout).subscribe(() => {
        const element = this.hubProfile.nativeElement as HTMLElement;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  gotoToolsSection(id: string): void {
    this.subs.sink = timer(this.consts.TOOLS_SECTION_TIMEOUT).subscribe(() =>
      document.getElementById(id).scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  }
}
